import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth } from '../firebase'; // Import Firestore and Auth instances
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  updateProfile,
  User,
} from 'firebase/auth';
import TimeDropdown from '../components/TimeDropdown';
import DateDropdown from '../components/DateDropdown';
import 'react-datepicker/dist/react-datepicker.css';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the Stripe object on every render.
const stripePromise = loadStripe('pk_test_51RlOSfCDWn9frLw22GlJQgLteFXn0Z1NRiaf3PmYEu5x5XLs1czE97PEdLlnKQgV5tfOKtiK9GDdNJtnnohHixxM00J5FoU9Ry');

// Service data (added a price for demonstration)
const services: { [key: string]: any } = {
  '1-on-1-lesson': {
    title: '1 on 1 Chess Lesson',
    description: 'Fine-tune your chess strategy for success with a personalized, one-on-one lesson with our expert coaches.',
    price: 3000 // $30.00
  },
  'free-trial': {
    title: 'Free Trial Class',
    description: 'Experience our coaching firsthand. Book this class for free as your first trial class.',
    price: 0
  },
  'weekly-lessons': {
    title: 'Weekly Chess Lessons',
    description: 'Commit to consistent improvement. Bring your chess game to the next level with our structured weekly lessons.',
    price: 3000, // $30.00
    recurring: true
  },
  'dance-class': {
    title: 'Dance Class',
    description: 'Join our dance class and learn from the best in the Greater Seattle Area.',
    price: 4000 // Example price in cents (e.g., $40.00)
  }
};

const CheckoutForm = ({ service, name, setName, email, setEmail, preferredDates, setPreferredDates, preferredTimes, setPreferredTimes, setIsSubmitted, setIsSubmitting, isSubmitting, serviceId }: any) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [location, setLocation] = useState('Online'); // Add location state



  const generateTimeSlots = () => {
    const slots = [];
    for (let i = 9; i <= 22; i++) {
      slots.push(`${i < 10 ? '0' : ''}${i}:00`);
      if (i < 22) {
        slots.push(`${i < 10 ? '0' : ''}${i}:30`);
      }
    }
    return slots;
  };

  const formatTime = (time: string) => {
    const [hour, minute] = time.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute < 10 ? '0' : ''}${minute} ${ampm}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setPaymentError(null);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (service.price > 0 && !cardElement) {
      setPaymentError("Card details not entered.");
      setIsSubmitting(false);
      return;
    }

    const combinedDateTimes = preferredDates
      .map((date: Date | null, index: number) => {
        const time = preferredTimes[index];
        if (!date || !time) return null;
        const [hour, minute] = time.split(':').map(Number);
        const newDateTime = new Date(date);
        newDateTime.setHours(hour, minute, 0, 0);
        return newDateTime;
      })
      .filter((dt: Date | null): dt is Date => dt !== null);

    if (combinedDateTimes.length !== 3) {
      setPaymentError("Please select a valid date and time for all three preferences.");
      setIsSubmitting(false);
      return;
    }

    try {
      if (service.price > 0) {
        // Create Payment Intent on your backend
        const response = await fetch('http://localhost:5000/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ amount: service.price }),
        });

        const { clientSecret, error: backendError } = await response.json();

        if (backendError) {
          setPaymentError(backendError);
          setIsSubmitting(false);
          return;
        }

        // Confirm the payment with Stripe
        const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement!, // Use non-null assertion as we checked for it above
            billing_details: {
              name: name,
              email: email,
            },
          },
        });

        if (error) {
          setPaymentError(error.message || "An unknown error occurred.");
          setIsSubmitting(false);
          return;
        } else if (paymentIntent && paymentIntent.status !== 'succeeded') {
          setPaymentError("Payment failed or was not successful.");
          setIsSubmitting(false);
          return;
        }
        // If payment succeeded, continue to save booking
        await addDoc(collection(db, "bookings"), {
          service: service.title,
          name: name,
          email: email,
          location: serviceId === 'dance-class' ? location : null,
          preferredDateTimes: combinedDateTimes,
          paymentIntentId: paymentIntent?.id,
          amount: service.price,
          currency: paymentIntent?.currency,
          submittedAt: serverTimestamp(),
          userId: auth.currentUser?.uid,
        });
      } else {
        // For free services, just save booking
        await addDoc(collection(db, "bookings"), {
          service: service.title,
          name: name,
          email: email,
          location: serviceId === 'dance-class' ? location : null,
          preferredDateTimes: combinedDateTimes,
          submittedAt: serverTimestamp(),
          userId: auth.currentUser?.uid,
        });
      }
      setIsSubmitted(true);
    } catch (error: any) {
      console.error("Error during payment or booking:", error);
      setPaymentError(error.message || "There was an error processing your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label htmlFor="name" className="form-label">Full Name</label>
        <input type="text" className="form-control" id="name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="mb-3">
        <label htmlFor="email" className="form-label">Email Address</label>
        <input type="email" className="form-control" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      {serviceId === 'dance-class' && (
        <div className="mb-3">
          <label className="form-label d-block">Location</label>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="radio" name="location" id="online" value="Online" checked={location === 'Online'} onChange={(e) => setLocation(e.target.value)} />
            <label className="form-check-label" htmlFor="online">Online</label>
          </div>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="radio" name="location" id="in-person" value="In-person" checked={location === 'In-person'} onChange={(e) => setLocation(e.target.value)} />
            <label className="form-check-label" htmlFor="in-person">In-person</label>
          </div>
        </div>
      )}
      <div className="mb-3">
        <label className="form-label d-block mb-2">Select Your Top Three Preferred Dates and Times</label>
        <div className="row">
          {preferredDates.map((date: Date | null, index: number) => (
            <div className="col-md-4" key={index}>
              <DateDropdown
                selectedDate={date}
                onDateChange={(d) => {
                  const newDates = [...preferredDates];
                  newDates[index] = d;
                  setPreferredDates(newDates);
                }}
                placeholderText="Select a date"
                minDate={new Date()}
                required
              />
              <TimeDropdown
                selectedTime={preferredTimes[index]}
                onTimeChange={(time) => {
                  const newTimes = [...preferredTimes];
                  newTimes[index] = time;
                  setPreferredTimes(newTimes);
                }}
                date={date}
                generateTimeSlots={generateTimeSlots}
                formatTime={formatTime}
                disabled={!date}
              />
            </div>
          ))}
        </div>
      </div>
      {service.price > 0 && (
        <div className="mb-3">
          <label className="form-label">Card Details</label>
          <CardElement className="form-control" />
          {paymentError && <div className="text-danger mt-2">{paymentError}</div>}
        </div>
      )}
      <button type="submit" className="btn btn-primary" disabled={isSubmitting || (service.price > 0 && !stripe)}>
        {isSubmitting ? 'Processing...' : (service.price > 0 ? `Pay $${(service.price / 100).toFixed(2)} & Confirm Booking` : 'Confirm Booking')}
      </button>
    </form>
  );
};

const Booking = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [preferredDates, setPreferredDates] = useState<(Date | null)[]>([null, null, null]);
  const [preferredTimes, setPreferredTimes] = useState<(string | null)[]>([null, null, null]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authFullName, setAuthFullName] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  if (!serviceId) {
    return <div className="container mt-5"><h2>Service not found</h2></div>;
  }

  const service = services[serviceId];

  if (!service) {
    return <div className="container mt-5"><h2>Service not found</h2></div>;
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      await updateProfile(userCredential.user, {
        displayName: authFullName,
      });
    } catch (error: any) {
      setAuthError(error.message);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
    } catch (error: any) {
      setAuthError(error.message);
    }
  };

  if (isSubmitted) {
    return (
      <div className="container mt-5 text-center">
        <h2>Thank You!</h2>
        <p>Your booking for a {service.title} has been received. We will contact you shortly to confirm the details.</p>
      </div>
    );
  }

  if (loadingAuth) {
    return <div className="container mt-5">Loading authentication...</div>;
  }

  return (
    <div className="container mt-5">
      <div className="row">
        <div className="col-md-6">
          <h2>Book Your Session</h2>
          <h3>{service.title}</h3>
          <p>{service.description}</p>
          {service.price > 0 && <p>Price: <strong>${(service.price / 100).toFixed(2)}{service.recurring && '/week'}</strong></p>}
          {serviceId === 'dance-class' && (
            <div className="mt-4">
              <h4>Class Details</h4>
              <p><strong>Location:</strong> Available online and in-person. The in-person address is: "Dance School Infiniti, 12815 NE 124th St Unit O, Kirkland, WA 98034".</p>
              <p><strong>Schedule:</strong> Select your top three preferred dates and times and we will get back to you shortly!</p>
              <p><strong>Pricing:</strong> $40 per class. Packages are also available.</p>
              <p><strong>Duration:</strong> 45 minutes.</p>
            </div>
          )}
        </div>
        <div className="col-md-6">
          <h4>Enter your details</h4>
          {!user ? (
            <div className="card p-4">
              <h5>Sign Up or Sign In to Book</h5>
              <form onSubmit={handleSignIn}>
                <div className="mb-3">
                  <label htmlFor="authFullName" className="form-label">Full Name (for Sign Up)</label>
                  <input type="text" className="form-control" id="authFullName" value={authFullName} onChange={(e) => setAuthFullName(e.target.value)} />
                </div>
                <div className="mb-3">
                  <label htmlFor="authEmail" className="form-label">Email</label>
                  <input type="email" className="form-control" id="authEmail" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="authPassword" className="form-label">Password</label>
                  <input type="password" className="form-control" id="authPassword" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} required />
                </div>
                {authError && <div className="text-danger mb-3">{authError}</div>}
                <button type="submit" className="btn btn-primary me-2">Sign In</button>
                <button type="button" className="btn btn-secondary" onClick={handleSignUp}>Sign Up</button>
              </form>
            </div>
          ) : (
            <Elements stripe={stripePromise}>
              <CheckoutForm
                service={service}
                serviceId={serviceId}
                name={name}
                setName={setName}
                email={email}
                setEmail={setEmail}
                preferredDates={preferredDates}
                setPreferredDates={setPreferredDates}
                preferredTimes={preferredTimes}
                setPreferredTimes={setPreferredTimes}
                setIsSubmitted={setIsSubmitted}
                setIsSubmitting={setIsSubmitting}
                isSubmitting={isSubmitting}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default Booking;